import React, { useState, useEffect, useRef } from 'react';
import './TagInput.css';

const TagInput = ({ tags = [], onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [internalTags, setInternalTags] = useState(tags);
  const inputRef = useRef(null);

  const normalizeTag = (value) => {
    const cleaned = (value || '')
      .replace(/^#+/, '')
      .trim()
      .toLowerCase()
      .replace(/^[^a-z0-9]+|[^a-z0-9-]+$/g, '');

    if (!cleaned) return '';
    if (!/^[a-z0-9-]+$/.test(cleaned)) return '';
    return cleaned;
  };

  const extractTags = (value) => {
    const raw = (value || '').trim();
    if (!raw) return [];

    const hashtagMatches = Array.from(raw.matchAll(/#([^\s#]+)/g))
      .map((m) => normalizeTag(m[1]))
      .filter(Boolean);

    if (hashtagMatches.length > 0) {
      return Array.from(new Set(hashtagMatches));
    }

    const plainTags = raw
      .split(/[\s,]+/)
      .map((part) => normalizeTag(part))
      .filter(Boolean);

    return Array.from(new Set(plainTags));
  };

  useEffect(() => {
    setInternalTags(tags);
  }, [tags]);

  const addTags = (newTagsRaw) => {
    const incoming = Array.from(new Set((newTagsRaw || []).map(normalizeTag).filter(Boolean)));
    if (!incoming.length) return;

    const merged = [...internalTags];
    incoming.forEach((tag) => {
      if (!merged.includes(tag)) {
        merged.push(tag);
      }
    });

    if (merged.length !== internalTags.length) {
      setInternalTags(merged);
      onChange && onChange(merged);
    }
  };

  const commitInputToTags = () => {
    const extracted = extractTags(inputValue);
    if (extracted.length > 0) {
      addTags(extracted);
      setInputValue('');
    }
  };

  const removeTag = (index) => {
    const newTags = internalTags.filter((_, i) => i !== index);
    setInternalTags(newTags);
    onChange && onChange(newTags);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitInputToTags();
    } else if (e.key === 'Backspace' && inputValue === '' && internalTags.length) {
      removeTag(internalTags.length - 1);
    }
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData?.getData('text') || '';
    const extracted = extractTags(pastedText);
    if (extracted.length > 0) {
      e.preventDefault();
      addTags(extracted);
      setInputValue('');
    }
  };

  return (
    <>
    <div className="tag-input" onClick={() => inputRef.current && inputRef.current.focus()}>
      {internalTags.map((t, i) => (
        <span key={i} className={`tag-pill${i >= 10 ? ' overtags' : ''}`} title={i >= 10 ? 'Only 10 tags are searchable' : ''}>
          {t}
          <span className="remove" onClick={() => removeTag(i)}>×</span>
        </span>
      ))}
      <input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={commitInputToTags}
        placeholder={internalTags.length === 0 ? placeholder : ''}
      />
    </div>
    {internalTags.length > 10 && (
      <p className="tag-input-overtags-warning">Only 10 tags are searchable — extra tags won't appear in search results.</p>
    )}
    </>
  );
};

export default TagInput;
